/**
 * Auth-reset reason taxonomy — shared between the client reset path
 * (`lib/auth-reset.ts`) and the server-side `middleware.ts`.
 *
 * This module is intentionally framework-agnostic (no `'use client'`, no React,
 * no browser globals) so the Next.js Edge middleware can import it.
 *
 * ## Terminal vs. transient (gh #1007)
 *
 * A reset is **terminal** when the session has genuinely ended and CJIS
 * requires a fresh interactive re-authentication before the user can proceed:
 *
 *   - `session-expired`   — 30-min idle lock max-dwell, 12h IA-11 absolute cap,
 *                           or an unexpected authenticated→null transition.
 *                           AC-11 (session lock) + IA-11 (reauthenticate after
 *                           idle / 12h) both mandate full re-auth here.
 *   - `manual-user-reset` — the user explicitly asked to clear the saved
 *                           browser session; honor that as a full teardown.
 *   - `no-app-access`     — the backend verified the bearer and refused the
 *                           account itself: 401 NO_APP_ACCESS (no app user,
 *                           deactivated, or no active tenant role — see
 *                           `lib/no-app-access.ts`). A fresh token gets the
 *                           same answer, so preserving Firebase state only let
 *                           the /login existing-session sync route the user
 *                           back to the dashboard to be refused again.
 *
 * For terminal reasons we destroy ALL local auth artifacts (Firebase
 * persistence via `clearLocalState()` + browser storage via middleware's
 * `Clear-Site-Data`) so the session cannot be silently resumed.
 *
 * A reset is **transient** when no idle/absolute timeout was reached — a
 * recoverable glitch such as a racing/stale bearer or a failed cookie POST:
 *
 *   - `profile-401`               — /api/auth/me 401'd (often a token race
 *                                   right after login).
 *   - `api-401-refresh-failed`    — an API 401 whose token refresh did not
 *                                   yield a usable bearer.
 *   - `login-session-sync-failed` — the POST /api/auth/session that establishes
 *                                   the cookie failed.
 *
 * For transient reasons we preserve Firebase persistence so the /login
 * existing-session sync (and `recoverSession()`) can silently re-establish the
 * cookie WITHOUT forcing a redundant password + TOTP round-trip. This does not
 * weaken CJIS: `recoverSession()` / `getIdToken()` still re-check the 12h cap
 * (`isSessionCapExceeded`) and the second-factor claim (`tokenHasSecondFactor`)
 * client-side, and `shared/auth.py` re-verifies the bearer on every call — so a
 * genuinely dead session degrades to full re-auth rather than resuming.
 */

export type AuthResetReason =
  | 'profile-401'
  | 'api-401-refresh-failed'
  | 'login-session-sync-failed'
  | 'manual-user-reset'
  // gh #876: authenticated→null observed by the client with no in-flight
  // fetch to trip api-client's 401 reset (12h IA-11 cap reached during a
  // silent Firebase token refresh, lost MFA claim, or token-read failure).
  | 'session-expired'
  // The backend answered 401 NO_APP_ACCESS for a verified bearer.
  | 'no-app-access';

export const TERMINAL_RESET_REASONS: readonly AuthResetReason[] = [
  'session-expired',
  'manual-user-reset',
  'no-app-access',
];

// The recoverable reasons — the ONLY inputs that preserve Firebase persistence
// for silent recovery. Everything else (including an absent or malformed
// reason) is treated as terminal.
export const TRANSIENT_RESET_REASONS: readonly AuthResetReason[] = [
  'profile-401',
  'api-401-refresh-failed',
  'login-session-sync-failed',
];

/**
 * True when a reset requires a full interactive re-authentication (terminal).
 *
 * Fail-closed: only the explicitly-transient reasons return false. An absent,
 * empty, or unrecognized reason defaults to TRUE (terminal) — the conservative,
 * CJIS-safe choice: when we cannot prove a reset is a recoverable glitch, force
 * re-auth rather than risk silently resuming a session.
 */
export function isTerminalResetReason(reason: string | null | undefined): boolean {
  if (!reason) {
    return true;
  }
  return !(TRANSIENT_RESET_REASONS as readonly string[]).includes(reason);
}

/**
 * True when a reset must proceed even inside the gh #946 freshness window (a
 * session established in the last 60s). The guard exists because a 401 right
 * after sign-in is usually a token race that clears on its own. It must not
 * hold up a reset that is not a race: explicit user intent, or the backend's
 * verdict that the account has no access — which arrives precisely inside the
 * window, straight after the sign-in that minted the session.
 */
export function bypassesFreshnessGuard(reason: AuthResetReason): boolean {
  return reason === 'manual-user-reset' || reason === 'no-app-access';
}
